import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Badge, Box, IconButton, Stack, Typography, styled } from "@mui/material";
import Icon from "@mui/material/Icon";
import { signOut } from "firebase/auth";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ZiplyLogoIcon from "../../assets/images/ziply-logo.svg";
import { auth } from "../../Config/firebase";
import { BUSINESS_LAC, LOGOUT_HEADING, LOGOUT_PRIMARYCONTENT } from "../../Constants/constants";
import { RootState } from "../../Redux/Store/Store";
import { clearLocalStorageExcept, deleteCookie, getFlags } from "../../Services/helper";
import {
  MenuBar,
  NavListStack,
  PageNameTypography,
  RowCenterStack,
  StyledA,
  StyledBar,
  StyledDialog,
  StyledDivBar,
  StyledDrawer,
  StyledElement,
  StyledLink,
  ZiplyLogo,
} from "../../Styles/GlobalStyles";
import { NavList, NavProps } from "../Navlist/NavList";
import { ConfirmationModel } from "../shared/ConfirmationModel/ConfirmationModel";
import { StyledTypography } from "../shared/CustomModal/CustomModalStlyes";
import { LoadingSpinner } from "../shared/LoadingSpinner/LoadingSpinner";
import PromosBanner from "../shared/PromosBanner/PromoBanner";
import { UpdateErrorComponent } from "../shared/UpdateErrorComponent/UpdateErrorComponent";
import AdminPieMenu from "./AdminPieMenu";
import WainingIcon from "../../assets/icons/SessionEnding.svg";
import Button from "../Button/Button";

const DEFAULT_TOTAL_INACTIVITY_SECONDS = 1200;
const DEFAULT_INACTIVITY_MODAL_SECONDS = 30;
const CROSS_TAB_ACTIVITY_KEY = "myaccount:lastActivityTs";

export interface LinkProps {
  nav?: boolean;
  active?: number;
}

export interface HeaderIconProps {
  outline?: number;
}
export const HeaderIcon = styled(Icon)<HeaderIconProps>(({ outline }) => ({
  borderRadius: "50%",
  border: outline ? "none" : "2px solid",
  padding: outline ? "0" : "2px",
  fontSize: outline ? "1.6rem" : "1.2rem",
}));

export const navLinks: NavProps[] = [
  {
    name: " Home",
    link: "/home",
    icon: (
      <HeaderIcon outline={+true}>
        <HomeOutlinedIcon fontSize="inherit" />
      </HeaderIcon>
    ),
  },
  {
    name: "Billing",
    link: "/billing",
    icon: (
      <HeaderIcon>
        <AttachMoneyIcon fontSize="inherit" />
      </HeaderIcon>
    ),
  },
  {
    name: "Account",
    link: "/account",
    icon: (
      <HeaderIcon>
        <PersonOutlinedIcon fontSize="inherit" />
      </HeaderIcon>
    ),
  },
  {
    name: "Messages",
    link: "/messages",
    icon: (
      <Badge variant="dot" color="error" overlap="circular">
        <HeaderIcon>
          <NotificationsOutlinedIcon fontSize="inherit" />
        </HeaderIcon>
      </Badge>
    ),
  },
];

export const Header = () => {
  const location = useLocation();
  const pathname = location?.pathname || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerDetails = useSelector(
    (state: any) => state?.customerInfoDetails?.givenNameFamilyName
  );
  const { activeAccount } = useSelector((state: any) => state?.customerInfoDetails);
  const { openSwitchModal } = useSelector((state: any) => state.appProposal);
  const [openLoader, setOpenLoader] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openError, setOpenError] = useState<boolean>(false);
  const [openInactivityModal, setOpenInactivityModal] = useState<boolean>(false);
  const [isInactivityLogoutInProgress, setIsInactivityLogoutInProgress] = useState<boolean>(false);
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityCountdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitializedInactivityRef = useRef(false);
  const [inactivityCountdown, setInactivityCountdown] = useState<number>(
    DEFAULT_INACTIVITY_MODAL_SECONDS
  );
  const [dialogData, setDialogData] = useState<any>({
    heading: "",
    primaryContent: "",
    containedButton: { onClick: () => {}, buttonType: "", buttonTitle: "" },
  });

  const commercial: boolean =
    window.location.pathname?.includes("/myaccount/commercial") ||
    window.location.pathname?.includes("/myaccount/select-account") ||
    window.location.pathname?.includes("/lac/select-account") ||
    window.location.pathname?.includes("/lac/commercial") ||
    window.location.pathname?.includes("/myaccount/acc-not-found") ||
    window.location.pathname?.includes("/lac/acc-not-found");

  const displaySelectAccount = sessionStorage.getItem("isMultiAccount");

  const handleCloseLoader = () => {
    setOpenLoader(false);
  };
  const handleOpenLoader = () => {
    setOpenLoader(true);
  };
  const adminName = localStorage.getItem("userName");
  const { isAuthenticated } = useSelector((state: RootState) => state.isAuthenticated);
  const { envFlags, paymentAmounts } = useSelector((state: any) => state?.userInfo);
  const INACTIVE_ACCOUNTS = getFlags("inactive_accounts", envFlags);
  const AUTO_LOGOUT = getFlags("auto_logout", envFlags);

  const getPaymentAmountSeconds = useCallback(
    (key: string, fallbackValue: number) => {
      const config = Array.isArray(paymentAmounts)
        ? paymentAmounts.find((item: any) => item?.key === key)
        : null;
      const parsedValue = Number(config?.value);

      if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return fallbackValue;
      }

      return parsedValue;
    },
    [paymentAmounts]
  );

  const totalInactivitySeconds = useMemo(
    () => getPaymentAmountSeconds("totalInactivity", DEFAULT_TOTAL_INACTIVITY_SECONDS),
    [getPaymentAmountSeconds]
  );

  const inactivityModalSeconds = useMemo(
    () => getPaymentAmountSeconds("inactivityModel", DEFAULT_INACTIVITY_MODAL_SECONDS),
    [getPaymentAmountSeconds]
  );

  const inactivityTimeoutMs = totalInactivitySeconds * 1000;
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const clearInactivityCountdownTimer = useCallback(() => {
    if (inactivityCountdownIntervalRef.current) {
      clearInterval(inactivityCountdownIntervalRef.current);
      inactivityCountdownIntervalRef.current = null;
    }
  }, []);

  const getLastCrossTabActivity = useCallback(() => {
    const value = Number(localStorage.getItem(CROSS_TAB_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : Date.now();
  }, []);

  const updateCrossTabActivity = useCallback(() => {
    localStorage.setItem(CROSS_TAB_ACTIVITY_KEY, String(Date.now()));
  }, []);

  const startInactivityTimer = useCallback(() => {
    if (isInactivityLogoutInProgress) {
      return;
    }

    clearInactivityTimer();
    const elapsed = Date.now() - getLastCrossTabActivity();
    const remainingTime = Math.max(inactivityTimeoutMs - elapsed, 0);

    inactivityTimeoutRef.current = setTimeout(() => {
      const latestElapsed = Date.now() - getLastCrossTabActivity();

      if (latestElapsed >= inactivityTimeoutMs && !isInactivityLogoutInProgress) {
        setOpenInactivityModal(true);
        return;
      }

      startInactivityTimer();
    }, remainingTime);
  }, [
    clearInactivityTimer,
    getLastCrossTabActivity,
    inactivityTimeoutMs,
    isInactivityLogoutInProgress,
  ]);

  const evaluateInactivityState = useCallback(() => {
    if (isInactivityLogoutInProgress) {
      return false;
    }

    const elapsed = Date.now() - getLastCrossTabActivity();

    if (elapsed >= inactivityTimeoutMs) {
      setOpenInactivityModal(true);
      return true;
    }

    return false;
  }, [getLastCrossTabActivity, inactivityTimeoutMs, isInactivityLogoutInProgress]);

  const handleInactivityModalClose = useCallback(() => {
    setIsInactivityLogoutInProgress(false);
    updateCrossTabActivity();
    setOpenInactivityModal(false);
    clearInactivityCountdownTimer();
    setInactivityCountdown(inactivityModalSeconds);
    startInactivityTimer();
  }, [
    clearInactivityCountdownTimer,
    inactivityModalSeconds,
    setIsInactivityLogoutInProgress,
    startInactivityTimer,
    updateCrossTabActivity,
  ]);

  useEffect(() => {
    if (!AUTO_LOGOUT) {
      setOpenInactivityModal(false);
      clearInactivityCountdownTimer();
      return;
    }

    if (!openInactivityModal) {
      clearInactivityCountdownTimer();
      return;
    }

    setInactivityCountdown(inactivityModalSeconds);
    inactivityCountdownIntervalRef.current = setInterval(() => {
      setInactivityCountdown((prev) => {
        if (prev <= 0) {
          clearInactivityCountdownTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInactivityCountdownTimer();
    };
  }, [AUTO_LOGOUT, openInactivityModal, clearInactivityCountdownTimer, inactivityModalSeconds]);

  useEffect(() => {
    if (!AUTO_LOGOUT) {
      clearInactivityTimer();
      return;
    }

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    const handleUserActivity = () => {
      if (openInactivityModal || isInactivityLogoutInProgress) {
        return;
      }

      updateCrossTabActivity();
      startInactivityTimer();
    };

    // Treat a fresh tab load (including browser refresh) as explicit activity once.
    if (!hasInitializedInactivityRef.current) {
      updateCrossTabActivity();
      setOpenInactivityModal(false);
      setInactivityCountdown(inactivityModalSeconds);
      hasInitializedInactivityRef.current = true;
    }

    if (!openInactivityModal) {
      startInactivityTimer();
    }

    events.forEach((eventName) => window.addEventListener(eventName, handleUserActivity));

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handleUserActivity));
      clearInactivityTimer();
    };
  }, [
    AUTO_LOGOUT,
    isInactivityLogoutInProgress,
    openInactivityModal,
    clearInactivityTimer,
    inactivityModalSeconds,
    startInactivityTimer,
    updateCrossTabActivity,
  ]);

  useEffect(() => {
    if (!AUTO_LOGOUT) {
      clearInactivityTimer();
      return;
    }

    const evaluateNow = () => {
      if (openInactivityModal || isInactivityLogoutInProgress) {
        return;
      }

      if (evaluateInactivityState()) {
        clearInactivityTimer();
      }
    };

    const intervalId = setInterval(evaluateNow, 1000);
    window.addEventListener("focus", evaluateNow);
    document.addEventListener("visibilitychange", evaluateNow);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", evaluateNow);
      document.removeEventListener("visibilitychange", evaluateNow);
    };
  }, [
    AUTO_LOGOUT,
    openInactivityModal,
    isInactivityLogoutInProgress,
    evaluateInactivityState,
    clearInactivityTimer,
  ]);

  useEffect(() => {
    if (!AUTO_LOGOUT) {
      return;
    }

    const handleStorageActivity = (event: StorageEvent) => {
      if (event.key !== CROSS_TAB_ACTIVITY_KEY) {
        return;
      }

      if (isInactivityLogoutInProgress) {
        return;
      }

      if (openInactivityModal) {
        handleInactivityModalClose();
        return;
      }

      startInactivityTimer();
    };

    window.addEventListener("storage", handleStorageActivity);

    return () => {
      window.removeEventListener("storage", handleStorageActivity);
    };
  }, [
    AUTO_LOGOUT,
    isInactivityLogoutInProgress,
    openInactivityModal,
    handleInactivityModalClose,
    startInactivityTimer,
  ]);

  const handleSignOut = useCallback(() => {
    handleOpenLoader();
    setOpen(false);
    setTimeout(async () => {
      await signOut(auth)
        .then(() => {
          // localStorage.removeItem("linkedAccountNumber");
          dispatch({ type: "USER_LOGOUT" });
          clearLocalStorageExcept(["myAccountOnly"]);
          deleteCookie("Token");
          deleteCookie("customerUSI");
          handleCloseLoader();
        })
        .catch((err) => {
          setIsInactivityLogoutInProgress(false);
          setOpenError(true);
          setOpen(false);
          handleCloseLoader();
          throw new Error(err);
        });
    }, 3000);
  }, [dispatch]);

  useEffect(() => {
    if (!AUTO_LOGOUT || !openInactivityModal || inactivityCountdown > 0) {
      return;
    }

    setIsInactivityLogoutInProgress(true);
    sessionStorage.removeItem("SESSION_EXPIRED_SHOWN");
    sessionStorage.setItem(
      "SESSION_EXPIRED_MESSAGE",
      "Your session has expired. Please log in again."
    );
    setOpenInactivityModal(false);
    clearInactivityCountdownTimer();
    handleSignOut();
  }, [
    AUTO_LOGOUT,
    openInactivityModal,
    inactivityCountdown,
    setIsInactivityLogoutInProgress,
    clearInactivityCountdownTimer,
    handleSignOut,
  ]);

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  };

  const handleDialog = () => {
    setIsDrawerOpen(false);
    setOpen(true);
    setDialogData({
      ...dialogData,
      heading: LOGOUT_HEADING,
      primaryContent: LOGOUT_PRIMARYCONTENT,
      containedButton: {
        ...dialogData?.containedButton,
        buttonType: "root",
        buttonTitle: "YES, LOG OUT",
      },
    });
  };

  const handleAdminSignOut = () => {
    window.location.replace("lac/admin/login");
    clearLocalStorageExcept(["myAccountOnly"]);
    dispatch({ type: "USER_LOGOUT" });
    deleteCookie("Token");
    deleteCookie("customerUSI");
  };
  const displayName = customerDetails?.givenName
    ? customerDetails.givenName[0]?.toUpperCase() + customerDetails.givenName.substring(1)
    : "";

  function handleExitAccount() {
    navigate("/adminscreen");
    dispatch({ type: "EXIT_ACCOUNT_LAC" });
    sessionStorage.removeItem("error");
    localStorage.removeItem("impersonatedEmail");
    window.location.reload();
  }

  const handleExitAccountToEnterprisePortal = () => {
    dispatch({ type: "EXIT_ACCOUNT_LAC" });
    sessionStorage.removeItem("error");
    localStorage.removeItem("impersonatedEmail");
    window.location.replace("/lac/enterpriseportal/impersonation");
  };

  const handleRoutingToHome = () => {
    if (
      !location?.pathname?.includes("acc-not-found") &&
      !location?.pathname?.includes("commercial")
    ) {
      navigate("/home");
    }
  };

  return (
    <>
      <Box position="fixed" width="100%" top="0" zIndex={openSwitchModal ? 0 : 1} bgcolor="white">
        <StyledBar
          width={{ sm: "97%", md: "80%" }}
          margin={"auto"}
          display={{ xs: "none", sm: "block" }}>
          <RowCenterStack mt={1} fontSize={"0.8rem"}>
            {isAuthenticated ? (
              <RowCenterStack>
                <IconButton color="inherit" size="small">
                  <PersonIcon
                    fontSize="inherit"
                    sx={{
                      color: "green",
                    }}
                  />
                </IconButton>
                <StyledTypography mr={2} color="green" fontSize={"14px"} fontWeight={"600"}>
                  Welcome, {adminName}
                </StyledTypography>
                {window.location.pathname !== "/lac/select-account" && (
                  <StyledElement
                    display={displaySelectAccount ? "block" : "none"}
                    onClick={() => navigate("/select-account")}
                    fontSize={"14px"}
                    fontWeight={600}>
                    Select account
                  </StyledElement>
                )}
              </RowCenterStack>
            ) : (
              <StyledTypography color="green" fontSize={"14px"} fontWeight={"600"}>
                My Account
              </StyledTypography>
            )}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                {isAuthenticated ? (
                  <AdminPieMenu
                    title="Admin Actions"
                    onSignOutAdmin={handleAdminSignOut}
                    onExitAccount={
                      BUSINESS_LAC ? handleExitAccountToEnterprisePortal : handleExitAccount
                    }
                  />
                ) : (
                  displayName && (
                    <StyledTypography mr={2} color="green" fontSize={"14px"} fontWeight={"600"}>
                      Welcome, {displayName}
                    </StyledTypography>
                  )
                )}
              </Box>
              <Box>
                {!isAuthenticated && (
                  <StyledElement
                    textcolor="black"
                    fontWeight={"600"}
                    fontSize={"14px"}
                    onClick={handleDialog}>
                    Log out
                  </StyledElement>
                )}
              </Box>
            </Stack>
          </RowCenterStack>
        </StyledBar>
        {open && (
          <ConfirmationModel
            dialogData={dialogData}
            setDialogData={setDialogData}
            openDialog={open}
            setOpenDialog={setOpen}
            onClick={handleSignOut}
          />
        )}
        {AUTO_LOGOUT && openInactivityModal && (
          <StyledDialog maxWidth="lg" open={openInactivityModal}>
            <Box padding={{ xs: "1rem 1rem 2rem 1rem", sm: "1rem 3rem 2rem 3rem" }}>
              <Stack gap={1} alignItems={"center"} justifyContent={"center"}>
                <img src={WainingIcon} alt="warning-icon" style={{ paddingBottom: "12px" }} />
                <Typography
                  fontSize={"22px"}
                  component={"span"}
                  textAlign={"center"}
                  fontWeight={600}>
                  Session Ending Soon
                </Typography>
                <Typography component={"p"} textAlign={"center"} mb={2} fontSize={"14px"}>
                  {`It seems you're inactive. Your session will end in ${inactivityCountdown} seconds.`}
                  &nbsp;
                </Typography>
                <Button
                  title={"i want to continue"}
                  type={"root"}
                  onClick={handleInactivityModalClose}
                />
              </Stack>
            </Box>
          </StyledDialog>
        )}
        <Box boxShadow={"0px 3px 4px 0px rgb(193, 193, 193)"} px={{ xs: "0.6rem", sm: "0" }}>
          <RowCenterStack width={{ sm: "100%", md: "82%" }} margin={"auto"}>
            <StyledA target="_self" onClick={handleRoutingToHome}>
              <ZiplyLogo height={"60px"} src={ZiplyLogoIcon} alt="ziply-logo" />
            </StyledA>
            <PageNameTypography fontSize={"14px"}>My Account</PageNameTypography>
            {!commercial && (
              <NavListStack direction={"row"}>
                <NavList navLinks={navLinks} />
              </NavListStack>
            )}
            <MenuBar ml={4} onClick={toggleDrawer(true)}>
              <MenuIcon />
            </MenuBar>
            <StyledDrawer anchor={"right"} open={isDrawerOpen} onClose={toggleDrawer(false)}>
              <RowCenterStack>
                <Stack direction={"row"} alignItems={"center"}>
                  <Typography
                    component={"p"}
                    flexShrink={1}
                    color="primary"
                    fontWeight="600"
                    fontSize={"0.8rem"}>
                    Welcome, {customerDetails?.givenName}
                  </Typography>
                </Stack>
                <IconButton onClick={toggleDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </RowCenterStack>
              <StyledElement
                textcolor="black"
                fontWeight={600}
                fontSize={"small"}
                onClick={handleDialog}
                ml={4}>
                Sign Out
              </StyledElement>
              <Stack ml={2.5} pt={6}>
                {navLinks?.map(({ name, link }) => {
                  const isDisabled =
                    INACTIVE_ACCOUNTS &&
                    name.toLowerCase() === "messages" &&
                    activeAccount === false;

                  const content = (
                    <>
                      <StyledLink
                        active={pathname === link || pathname?.includes(link) ? +true : undefined}
                        to={link}
                        style={{
                          pointerEvents: isDisabled ? "none" : "auto",
                          opacity: isDisabled ? 0.5 : 1,
                        }}>
                        {name}
                      </StyledLink>
                      {pathname === link || pathname?.includes(link) ? <StyledDivBar /> : null}
                    </>
                  );

                  return (
                    <Box width={"min-content"} key={name}>
                      <Typography component={"p"} py={3} fontSize={"0.6rem"} fontWeight="600">
                        {content}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </StyledDrawer>
          </RowCenterStack>
        </Box>
        <PromosBanner />
      </Box>
      {openLoader && <LoadingSpinner />}
      {openError && <UpdateErrorComponent openError={openError} setOpenError={setOpenError} />}
    </>
  );
};
